import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import Stripe from 'stripe';
import { AppConfigService } from '../providers/config.service';
import { PlanPrice } from '../types/plan.type';
import { BillingFrequency, Product, ProductPrice } from '../types/product.type';
import { Promotion } from '../types/promotion.type';
import { DataLoaderService } from './data-loader.service';
import { FeatureFlagsService } from './feature-flags.service';
import { StateService } from './state.service';
import { TransferStateService } from './transfer-state.service';

export type PriceDetails = {
  basePlan: number;
  storageAddonCost: number;
  subTotal: number;
  yearlyDiscount: number;
  promoDiscount: number;
  totalDiscount: number;
  balance: number;
  total: number;
};

@Injectable({
  providedIn: 'root',
})
export class PricingService {
  constructor(
    private config: AppConfigService,
    private http: HttpClient,
    private transferState: TransferStateService,
    private loader: DataLoaderService,
    private state: StateService,
    private featureFlags: FeatureFlagsService
  ) {}

  public async getPrices(product_type: 'main' | 'storage_addon') {
    const vals = await Promise.all([
      this.loader.get(`prices_${product_type}_year`, () =>
        this.getPricesInternal({
          product_type,
          recurring_interval: 'year',
        })
      ),
      this.loader.get(`prices_${product_type}_month`, () =>
        this.getPricesInternal({
          product_type,
          recurring_interval: 'month',
        })
      ),
    ]);

    const prices = [...vals[0], ...vals[1]];

    prices.forEach(price => {
      if (price.recurring.interval === 'month') price.billingFrequency = 'monthly';
      else price.billingFrequency = 'yearly';
    });

    return prices;
  }

  public convertPrice(price: Stripe.Price): ProductPrice {
    const billingFrequency = price.recurring.interval === 'month' ? 'monthly' : 'yearly';
    return {
      amount: price.unit_amount / 100,
      billingFrequency,
      id: price.id,
      product: price.product as string,
      lookupKey: price.lookup_key,
    };
  }

  private async getPricesInternal(data: {
    product_type: 'main' | 'storage_addon';
    recurring_interval: 'month' | 'year';
  }) {
    const url = `${this.config.apiEndpoint}/billing/prices?product_type[]=${data.product_type}&limit=100&recurring_interval=${data.recurring_interval}`;
    return this.transferState.withStateTransfer(
      'prices_' + data.product_type + '_' + data.recurring_interval,
      async () => {
        const resp = await lastValueFrom(
          this.http.get<{ data: (Stripe.Price & { billingFrequency: 'monthly' | 'yearly' })[] }>(url)
        );
        return resp.data;
      }
    );
  }

  /**
   * Returns the monthly price of the product with yearly billing cycle.
   */
  public async getPlanPrice(product: Product) {
    const prices = await this.getPrices('main');
    const price = prices.find(p => p.product === product.id && p.billingFrequency === 'yearly');
    return price.unit_amount / 100 / 12;
  }

  public async calcPriceDetails(price: ProductPrice, promo?: Promotion): Promise<PriceDetails> {
    const [productPrices, addonPrices] = await Promise.all([this.getPrices('main'), this.getPrices('storage_addon')]);
    const storageAddonPrice = addonPrices.find(_price => _price.billingFrequency === price.billingFrequency);
    const storageAddon = this.state.plan.storageAddon;

    const prices = {
      monthly: productPrices.find(_price => _price.product === price.product && _price.billingFrequency === 'monthly'),
      yearly: productPrices.find(_price => _price.product === price.product && _price.billingFrequency === 'yearly'),
    };

    const basePlan = prices.monthly.unit_amount * (price.billingFrequency === 'yearly' ? 12 : 1);
    const basePlanReal = prices[price.billingFrequency].unit_amount;
    const yearlyDiscount = basePlan - basePlanReal;
    const promoDiscount = this.getPromoDiscount(price, promo) * 100;

    const storageAddonCost = storageAddon ? storageAddon.quantity * storageAddonPrice.unit_amount : 0;

    const subTotal = basePlan + storageAddonCost;
    const totalDiscount = yearlyDiscount + promoDiscount;
    let total = basePlanReal + storageAddonCost - promoDiscount;
    if (total < 0) total = 0;

    let balance = this.state.balance || 0;

    if (total > 0) {
      if (total > balance) {
        total -= balance;
      } else {
        balance = total;
        total = 0;
      }
    }

    return {
      basePlan: basePlan / 100,
      storageAddonCost: storageAddonCost / 100,
      subTotal: subTotal / 100,
      yearlyDiscount: yearlyDiscount / 100,
      promoDiscount: promoDiscount / 100,
      totalDiscount: totalDiscount / 100,
      balance: balance / 100,
      total: total / 100,
    };
  }

  private getPromoDiscount(price: ProductPrice, promo: Promotion): number {
    if (!promo) return 0;
    if (promo.amountOff) return promo.amountOff;
    return (price.amount / 100) * promo.percentOff;
  }

  public isDowngrade(fromPrice: PlanPrice, to: Product) {
    const newMonthlyPrice = to.prices.find(p => p.billingFrequency === fromPrice.billingFrequency);
    return newMonthlyPrice.amount < fromPrice.amount;
  }

  public isUpgrade(fromPrice: PlanPrice, to: Product) {
    return !this.isDowngrade(fromPrice, to);
  }

  public getTrialLengthDays() {
    if (this.featureFlags.get('noTrial')) return undefined;
    if (this.featureFlags.get('shortTrial')) return 7;
    return 14;
  }

  getPriceDetails(
    prices: ProductPrice[],
    promo: Promotion,
    billingFrequency: BillingFrequency
  ): {
    monthlyAmount: number;
    monthlyAmountWithoutDiscount: number;
    yearlyAmount: number;
    yearlyAmountWithoutDiscount: number;
    monthlyDiscountAmount: number;
    displayAmountWhole: number;
    displayAmountCents: number;
  } {
    const monthlyPrice = prices.find(price => price.billingFrequency === 'monthly');
    const yearlyPrice = prices.find(price => price.billingFrequency === 'yearly');

    let monthlyDiscountAmount = 0;
    let yearlyDiscountAmount = 0;
    if (promo?.percentOff) {
      monthlyDiscountAmount = (monthlyPrice.amount / 100) * promo.percentOff;
      yearlyDiscountAmount = (yearlyPrice.amount / 100) * promo.percentOff;
    }
    // Not showing fixed discount amounts here on purpose.

    const monthlyAmountWithoutDiscount = monthlyPrice.amount;
    const yearlyAmountWithoutDiscount = yearlyPrice.amount;
    const monthlyAmount = monthlyPrice.amount - monthlyDiscountAmount;
    const yearlyAmount = yearlyPrice.amount - yearlyDiscountAmount;

    const displayAmount = billingFrequency === 'yearly' ? yearlyAmount / 12 : monthlyAmount;
    const displayAmountWhole = Math.floor(displayAmount);
    const displayAmountCents = Math.round((displayAmount - displayAmountWhole) * 100);

    return {
      monthlyAmount,
      monthlyAmountWithoutDiscount,
      yearlyAmount,
      yearlyAmountWithoutDiscount,
      monthlyDiscountAmount,
      displayAmountWhole,
      displayAmountCents,
    };
  }
}
